<script setup>
import { onMounted, ref, inject } from 'vue';
import ModalForm from './elements/ModalForm.vue';
import RecordTable from './elements/RecordTable.vue';
import { formatDate } from '../helpers';
import axios from 'axios';

const env = inject('env', {
    currentSubscription: null,
    refTemplateOptions: '{}',
});

onMounted(() => {
    loadSubscriptions();
    getTemplates();
});

const addFormModalId = "addPromptOptionTemplateModal";
const deleteFormId = "prompt-option-template-delete-modal";

let templates = ref({ data: [] });
let loading = ref(0);
let sortProperty = ref('title');
let sortAsc = ref(true);
let subscriptionFilter = ref('__null');
let subscriptionList = ref({});
let currentTemplate = ref(null);
let refTemplateOptions = ref({});
try {
    refTemplateOptions.value = typeof env.refTemplateOptions === 'string'
        ? JSON.parse(env.refTemplateOptions)
        : env.refTemplateOptions;
} catch (e) {
    console.error('Error parsing refTemplateOptions', e);
}

let addErrors = ref({});
let newTemplate = ref({
    title: '',
    prompt_base: '',
    reference_template_id: '',
    subscription_id: ''
});

function loadSubscriptions() {
    loading.value++;
    axios.get('/ajax/subscriptions', {})
        .then((response) => {
            subscriptionList.value = {};
            for (let i = 0; i < response.data.length; i++) {
                subscriptionList.value[response.data[i].id] = response.data[i].name;
            }
            loading.value--;
        })
        .catch(function () {
            loading.value--;
        });
}

function changeFilter() {
    getTemplates();
}

function getTemplates(page = 1) {
    loading.value++;
    const params = {
        sort: sortProperty.value,
        sort_dir: sortAsc.value ? 'asc' : 'desc',
        page,
    };
    if (subscriptionFilter.value === '__null') {
        params.no_subscription = 1;
    } else if (subscriptionFilter.value) {
        params.subscription_id = subscriptionFilter.value;
    }

    axios.get('/ajax/promptOptionTemplates', { params })
        .then((response) => {
            templates.value = response.data;
            loading.value--;
        })
        .catch(function (error) {
            loading.value--;
            console.error(error);
        });
}

function onDeleteClick(template) {
    currentTemplate.value = template;
    $('#' + deleteFormId).modal();
}

function onDeleteConfirm() {
    if (!currentTemplate.value) {
        return;
    }
    loading.value++;
    axios.delete(`/ajax/promptOptionTemplates/${currentTemplate.value.id}`)
        .then(() => {
            loading.value--;
            $('#' + deleteFormId).modal('hide');
            getTemplates();
        })
        .catch((error) => {
            loading.value--;
            console.error(error);
        });
}

function showAddForm() {
    // reset form state and errors
    newTemplate.value = {
        title: '',
        prompt_base: '',
        reference_template_id: '',
        subscription_id: ''
    };
    // preselect subscription based on filter
    if (subscriptionFilter.value && subscriptionFilter.value !== '__null') {
        newTemplate.value.subscription_id = subscriptionFilter.value;
    }
    addErrors.value = {};
    $('#' + addFormModalId).modal();
}

function onCreateFormSubmit(submitEvent) {
    const formData = new FormData(submitEvent.target);
    loading.value++;
    axios.post('/ajax/promptOptionTemplates', formData)
        .then(() => {
            loading.value--;
            $('#' + addFormModalId).modal('hide');
            getTemplates();
        })
        .catch((error) => {
            loading.value--;
            if (error.response && error.response.data && error.response.data.errors) {
                addErrors.value = error.response.data.errors;
            }
            console.error(error);
        });
}

function changeSorting(property) {
    if (property !== sortProperty.value) {
        sortProperty.value = property;
        sortAsc.value = true;
    } else {
        sortAsc.value = !sortAsc.value;
    }
    getTemplates();
}

function changePage(page) {
    getTemplates(page);
}

const tableLayout = [
    { title: 'Name', property: 'title', sortable: true },
    { title: 'Mandant', property: 'subscription_id', map: subscriptionList },
    { title: 'Referenziert', property: 'reference_template_title' },
    { title: 'Zuletzt bearbeitet', property: 'updated_at', format: formatDate, sortable: true },
];

const tableActions = [
    { title: 'Bearbeiten', name: 'edit', btnClass: 'btn-outline-primary' },
    { title: 'Löschen', name: 'delete', btnClass: 'btn-outline-danger' },
];

function onTableAction(action, record) {
    if (action === 'edit') {
        window.location.href = `/promptOptionTemplates/${record.id}/edit`;
    } else if (action === 'delete') {
        onDeleteClick(record);
    }
}
</script>

<template>
    <div class="d-flex justify-content-between align-items-center mb-3">
        <a href="/promptSnippets" class="">Optionen bearbeiten</a>
    </div>

    <div class="row mb-3">
        <div class="col-4">
            <div class="input-group">
                <label>
                    Mandant
                    <select class="form-control" name="subscription_id" v-model="subscriptionFilter"
                        @change="changeFilter">
                        <option value="">alles</option>
                        <option value="__null">default Vorlagen</option>
                        <option v-for="(subscriptionName, index) in subscriptionList" :value="index">
                            {{ subscriptionName }}
                        </option>
                    </select>
                </label>
            </div>
        </div>
        <div class="col-4"></div>
        <div class="col-4 text-right align-self-right">
            <button type="button" class="btn btn-primary" @click.prevent="showAddForm">
                Vorlage anlegen
            </button>
        </div>
    </div>

    <RecordTable :records="templates" :layout="tableLayout" :actions="tableActions" :loading="loading"
        :sort-property="sortProperty" :sort-asc="sortAsc"
        @header-click="changeSorting" @action="onTableAction" @page-click="changePage">
    </RecordTable>

    <ModalForm :modal-id="addFormModalId" modal-title="Vorlage erstellen" modal-class="" submit-text="Erstellen"
        dismiss-text="Abbrechen" :loading="loading" @form-submit="onCreateFormSubmit">
        <div class="input-group mb-3">
            <input class="form-control" type="text" name="title" placeholder="Vorlagen-Titel"
                aria-label="Vorlagen-Titel" v-model="newTemplate.title" required />
        </div>
        <div class="text-danger small mb-2" v-if="addErrors.title">
            {{ addErrors.title[0] }}
        </div>
        <div class="input-group mb-3">
            <textarea class="form-control" name="prompt_base" placeholder="Basis-Prompt" aria-label="Basis-Prompt"
                v-model="newTemplate.prompt_base"></textarea>
        </div>
        <div class="text-danger small mb-2" v-if="addErrors.prompt_base">
            {{ addErrors.prompt_base[0] }}
        </div>
        <div class="input-group mb-3">
            <select class="form-control" name="reference_template_id" v-model="newTemplate.reference_template_id">
                <option value="">Keine Referenz</option>
                <option v-for="(title, id) in refTemplateOptions" :key="id" :value="id">
                    {{ title }}
                </option>
            </select>
        </div>
        <div class="text-danger small mb-2" v-if="addErrors.reference_template_id">
            {{ addErrors.reference_template_id[0] }}
        </div>
        <div class="input-group mb-3">
            <select class="form-control" name="subscription_id" v-model="newTemplate.subscription_id">
                <option value="">kein Mandant</option>
                <option v-for="(subscriptionName, index) in subscriptionList" :value="index">
                    {{ subscriptionName }}
                </option>
            </select>
        </div>
        <div class="text-danger small mb-2" v-if="addErrors.subscription_id">
            {{ addErrors.subscription_id[0] }}
        </div>
    </ModalForm>

    <ModalForm :modal-id="deleteFormId" modal-title="Vorlage löschen" modal-class="modal-lg" submit-text="ja"
        dismiss-text="nein" :loading="loading" @form-submit="onDeleteConfirm">
        Wollen Sie die Vorlage "{{ currentTemplate ? currentTemplate.title : '' }}" wirklich löschen?
    </ModalForm>
</template>
